import tripStatuses from '../../trip-statuses';

export default function TripItemFlightRecommendationService(
    $filter,
    userService,
    flightRecommendationService,
    flightGroupService,
) {
    'ngInject';

    Object.assign(this, {
        getFlightRecommendationAirportCodeRoute,
        getFlightRecommendationAirportCityRoute,
        getFlightAirportCodeRoute,
        getFlightSegmentAirportCodeRoute,
        getRecommendationDepartureDate,
        getRecommendationArrivalDate,
        isOneWayTrip,
        isRoundTrip,
        isOutwardGroup,
        isInwardGroup,
        getFirstFlight,
        getLastFlight,
        getDepartureArrivalDates,
        findFlightStop,
        isHoldUntilVisible,
        isOutwardDepartEqualInwardArrival,
        isInwardDepartEqualOutwardArrival,
    });

    function getFirstFlight(recommendation) {
        return recommendation
            && recommendation.FlightGroups
            && recommendation.FlightGroups.length
            && recommendation.FlightGroups[0].Flights
            && recommendation.FlightGroups[0].Flights.length
            && recommendation.FlightGroups[0].Flights[0]
            || null;
    }

    function getDepartureArrivalDates(recommendation) {
        const firstFlight = getFirstFlight(recommendation);
        const lastFlight = getLastFlight(recommendation);
        const rs = userService.getRegionalSettings();
        const firstFlightDepartureDate = moment(firstFlight.DepartureDate).format(rs.shortDayMonthDateFormatMoment);
        const lastFlightDepartureDate = moment(lastFlight.DepartureDate).format(rs.shortDayMonthDateFormatMoment);

        return isOneWayTrip(recommendation)
            ? firstFlightDepartureDate
            : `${firstFlightDepartureDate} - ${lastFlightDepartureDate}`;
    }

    function getLastFlight(recommendation) {
        if (!(recommendation
            && recommendation.FlightGroups
            && recommendation.FlightGroups.length)) {
            return null;
        }
        const lastGroupIndex = recommendation.FlightGroups.length - 1;
        const lastGroup = recommendation.FlightGroups[lastGroupIndex];
        if (!lastGroup.Flights || !lastGroup.Flights.length) {
            return null;
        }

        return lastGroup.Flights[lastGroup.Flights.length - 1];
    }

    function isOneWayTrip(recommendation) {
        return recommendation.FlightGroups.length === 1;
    }

    function isRoundTrip(recommendation) {
        if (!recommendation || !recommendation.FlightGroups || recommendation.FlightGroups.length !== 2) {
            return false;
        }
        const firstFlight = getFirstFlight(recommendation);
        const lastFlight = getLastFlight(recommendation);

        return firstFlight
            && lastFlight
            && firstFlight.DepartureCityCode === lastFlight.ArrivalCityCode;
    }

    function getFlightRecommendationAirportCodeRoute(flightRecommendation) {
        return getFlightRecommendationStops(flightRecommendation, 'DepartureAirportCode', 'ArrivalAirportCode');
    }

    function getFlightRecommendationAirportCityRoute(flightRecommendation) {
        return getFlightRecommendationStops(flightRecommendation, 'DepartureAirportCity', 'ArrivalAirportCity');
    }

    function getFlightSegmentAirportCodeRoute(flightGroup) {
        const flightRecommendation = {FlightGroups: [flightGroup]};
        return getFlightSegmentStops(flightRecommendation, 'DepartureAirportCode', 'ArrivalAirportCode');
    }

    function getFlightRecommendationStops(flightRecommendation, fieldDeparture, fieldArrival) {
        if (!(flightRecommendation && flightRecommendation.FlightGroups)) {
            return [];
        }

        const getAppropriateFlightStops = function(recommendation, first, last, index, array) {
            if (isRoundTrip(recommendation)) {
                return (index === array.length - 1) ? [last] : [first, last];
            } else if (index > 0 && array[index - 1].last.trim() === first.trim()) {
                return [last];
            }

            return [first, last];
        };

        return flightRecommendation.FlightGroups
            .filter((group) => !!(group && group.Flights && group.Flights.length))
            .reduce((acc, flightGroup) => [
                ...acc,
                {
                    first: flightGroup.Flights[0][fieldDeparture],
                    last: flightGroup.Flights[flightGroup.Flights.length - 1][fieldArrival],
                },
            ], [])
            .reduce((acc, {first, last}, index, array) => [
                ...acc,
                ...getAppropriateFlightStops(flightRecommendation, first, last, index, array),
            ], []);
    }

    function getFlightSegmentStops(flightRecommendation, fieldDeparture, fieldArrival) {
        if (!(flightRecommendation && flightRecommendation.FlightGroups)) {
            return [];
        }

        const allStops = flightRecommendation.FlightGroups
            .filter((group) => !!(group && group.Flights && group.Flights.length))
            .reduce((acc, flightGroup) => [...acc, ...flightGroup.Flights], []);
        const lastStop = allStops[allStops.length - 1];
        return [
            ...allStops.map((stop) => stop[fieldDeparture]),
            ...lastStop && lastStop[fieldArrival] || [],
        ];
    }

    function getRecommendationDepartureDate(recommendation) {
        const firstFlight = recommendation.FlightGroups[0].Flights[0];

        return firstFlight.DepartureDate;
    }

    function getRecommendationArrivalDate(recommendation) {
        const lastGroup = recommendation.FlightGroups[recommendation.FlightGroups.length - 1];
        const lastFlight = lastGroup.Flights[lastGroup.Flights.length - 1];

        return lastFlight.ArrivalDate;
    }

    function getFlightAirportCodeRoute({DepartureAirportCode, ArrivalAirportCode, Touchdowns}) {
        const touchdownCodes = Touchdowns
            && Touchdowns.map((touchdown) => (`${touchdown.AirportCode} (Touchdown)`)) || [];
        return [DepartureAirportCode, ...touchdownCodes, ArrivalAirportCode];
    }

    function findFlightStop(flightGroup, city) {
        const stop = flightGroup.FlightStops.find((flightStop) => flightStop.City === city);
        return stop || {};
    }

    function isHoldUntilVisible(flightRecommendation, tripDetails) {
        const {HELD} = tripStatuses;
        return (flightRecommendation.Status === HELD || tripDetails.Status === HELD)
            && !!flightRecommendation.HoldUntil;
    }

    function isOutwardDepartEqualInwardArrival(currentGroup, flightRecommendation, isCurrentGroupOutwardGroup, isCurrentGroupInwardGroup) {
        if (isCurrentGroupOutwardGroup) {
            const outwardFirstFlight = flightGroupService.getFirstFlight(currentGroup);
            const inwardGroup = flightRecommendationService.getInwardGroup(flightRecommendation);
            const inwardLastFlight = flightGroupService.getLastFlight(inwardGroup);

            return outwardFirstFlight.DepartureAirportCode === inwardLastFlight.ArrivalAirportCode;
        } else if (isCurrentGroupInwardGroup) {
            const inwardFirstFlight = flightGroupService.getLastFlight(currentGroup);
            const outwardGroup = flightRecommendationService.getOutwardGroup(flightRecommendation);
            const outwardLastFlight = flightGroupService.getFirstFlight(outwardGroup);
            return outwardLastFlight.DepartureAirportCode === inwardFirstFlight.ArrivalAirportCode;
        }

        return false;
    }

    function isInwardDepartEqualOutwardArrival(currentGroup, flightRecommendation, isCurrentGroupOutwardGroup, isCurrentGroupInwardGroup) {
        if (isCurrentGroupOutwardGroup) {
            const outwardLastFlight = flightGroupService.getLastFlight(currentGroup);
            const inwardGroup = flightRecommendationService.getInwardGroup(flightRecommendation);
            const inwardFirstFlight = flightGroupService.getFirstFlight(inwardGroup);
            return outwardLastFlight.ArrivalAirportCode === inwardFirstFlight.DepartureAirportCode;
        } else if (isCurrentGroupInwardGroup) {
            const inwardFirstFlight = flightGroupService.getFirstFlight(currentGroup);
            const outwardGroup = flightRecommendationService.getOutwardGroup(flightRecommendation);
            const outwardLastFlight = flightGroupService.getLastFlight(outwardGroup);
            return outwardLastFlight.ArrivalAirportCode === inwardFirstFlight.DepartureAirportCode;
        }

        return false;
    }


    function isOutwardGroup(group, recommendation) {
        return group === flightRecommendationService.getOutwardGroup(recommendation);
    }

    function isInwardGroup(group, recommendation) {
        return group === flightRecommendationService.getInwardGroup(recommendation);
    }
}



// WEBPACK FOOTER //
// ./src/app/modules/trip/trip-item-recommendation/trip-item-flight-recommendation/trip-item-flight-recommendation-service.js